import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertTriangle, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { PHONE_EXAMPLE_PRETTY, PHONE_PLACEHOLDER } from '../../config/region';
import { useInviteAcceptPage } from './useInviteAcceptPage';

const shellClasses =
  'min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 px-4 py-10';
const cardClasses =
  'w-full max-w-md bg-gradient-to-br from-slate-800/60 to-slate-900/80 backdrop-blur border border-slate-700/50 rounded-2xl shadow-2xl p-6 sm:p-8';
const inputClass =
  'w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500';

const StatusCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  children: React.ReactNode;
}> = ({ icon, iconBg, title, children }) => (
  <div className={shellClasses}>
    <div className={cardClasses}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
        <h1 className="text-lg font-semibold text-white">{title}</h1>
      </div>
      <div className="text-sm text-gray-300 space-y-3">{children}</div>
    </div>
  </div>
);

const InviteAcceptPage: React.FC = () => {
  const {
    view,
    form,
    fieldErrors,
    submitting,
    submitError,
    updateField,
    submit,
    goToLogin,
  } = useInviteAcceptPage();

  if (view.kind === 'loading') {
    return (
      <div className={shellClasses}>
        <div className={cardClasses}>
          <div className="animate-pulse space-y-3">
            <div className="h-6 w-40 bg-white/10 rounded" />
            <div className="h-4 w-64 bg-white/5 rounded" />
            <div className="h-28 bg-white/5 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (view.kind === 'error') {
    return (
      <StatusCard
        icon={<AlertTriangle className="w-5 h-5 text-amber-300" />}
        iconBg="bg-amber-500/20"
        title="Couldn't load invite"
      >
        <p>{view.message}</p>
        <Link to="/login" className="inline-block text-cyan-400 hover:text-cyan-300 underline">
          Back to login
        </Link>
      </StatusCard>
    );
  }

  if (view.kind === 'expired') {
    return (
      <StatusCard
        icon={<Clock className="w-5 h-5 text-amber-300" />}
        iconBg="bg-amber-500/20"
        title="This invite has expired"
      >
        <p>Invites are valid for 7 days. Ask the admin who invited you to resend the invite.</p>
        <Link to="/login" className="inline-block text-cyan-400 hover:text-cyan-300 underline">
          Back to login
        </Link>
      </StatusCard>
    );
  }

  if (view.kind === 'revoked') {
    return (
      <StatusCard
        icon={<XCircle className="w-5 h-5 text-red-300" />}
        iconBg="bg-red-500/20"
        title="This invite was revoked"
      >
        <p>Ask the admin who invited you to send a new one.</p>
        <Link to="/login" className="inline-block text-cyan-400 hover:text-cyan-300 underline">
          Back to login
        </Link>
      </StatusCard>
    );
  }

  if (view.kind === 'already_accepted') {
    return (
      <StatusCard
        icon={<CheckCircle2 className="w-5 h-5 text-green-300" />}
        iconBg="bg-green-500/20"
        title="Invite already used"
      >
        <p>This invite has already been accepted. Log in with your email and password to continue.</p>
        <Link to="/login" className="inline-block text-cyan-400 hover:text-cyan-300 underline">
          Go to login
        </Link>
      </StatusCard>
    );
  }

  if (view.kind === 'not_found') {
    return (
      <StatusCard
        icon={<AlertTriangle className="w-5 h-5 text-red-300" />}
        iconBg="bg-red-500/20"
        title="Invite not found"
      >
        <p>The invite link is invalid. Double-check the URL, or ask the admin to resend.</p>
        <Link to="/login" className="inline-block text-cyan-400 hover:text-cyan-300 underline">
          Back to login
        </Link>
      </StatusCard>
    );
  }

  if (view.kind === 'email_taken') {
    return (
      <StatusCard
        icon={<Mail className="w-5 h-5 text-cyan-300" />}
        iconBg="bg-cyan-500/20"
        title="You already have an account"
      >
        <p>
          An account already exists for{' '}
          {view.email ? <span className="font-semibold text-white">{view.email}</span> : 'this email'}.
          Log in with your existing password to continue.
        </p>
        <button
          type="button"
          onClick={() => goToLogin(view.email)}
          className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium"
        >
          Go to login
        </button>
      </StatusCard>
    );
  }

  // view.kind === 'ready'
  const { lookup } = view;
  const expiresFormatted = lookup.expires_at
    ? new Date(lookup.expires_at).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  return (
    <div className={shellClasses}>
      <div className={cardClasses}>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Mail className="w-5 h-5 text-purple-300" />
          </div>
          <h1 className="text-lg font-semibold text-white">Join {lookup.workspace_name ?? 'the workspace'}</h1>
        </div>
        <p className="text-sm text-gray-300 mb-4">
          You've been invited to <span className="font-medium text-white">{lookup.workspace_name}</span>
          {lookup.role ? (
            <>
              {' '}as{' '}
              <span className="px-2 py-0.5 text-xs rounded-full border bg-purple-500/10 text-purple-300 border-purple-500/30">
                {lookup.role}
              </span>
            </>
          ) : null}
          . Set a password and we'll log you in.
          {expiresFormatted && (
            <span className="block mt-1 text-xs text-gray-400">Invite expires {expiresFormatted}.</span>
          )}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Email</label>
            <input
              type="email"
              value={lookup.email ?? ''}
              readOnly
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 text-sm cursor-not-allowed"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="accept-first" className="block text-xs font-medium text-gray-400 mb-1">
                First name
              </label>
              <input
                id="accept-first"
                type="text"
                value={form.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
                required
                disabled={submitting}
                className={inputClass}
                placeholder="First name"
              />
              {fieldErrors.first_name && (
                <p className="mt-1 text-xs text-red-300">{fieldErrors.first_name}</p>
              )}
            </div>
            <div>
              <label htmlFor="accept-last" className="block text-xs font-medium text-gray-400 mb-1">
                Last name
              </label>
              <input
                id="accept-last"
                type="text"
                value={form.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
                required
                disabled={submitting}
                className={inputClass}
                placeholder="Last name"
              />
              {fieldErrors.last_name && (
                <p className="mt-1 text-xs text-red-300">{fieldErrors.last_name}</p>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="accept-phone" className="block text-xs font-medium text-gray-400 mb-1">
              Mobile number
            </label>
            <input
              id="accept-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              required
              disabled={submitting}
              className={inputClass}
              placeholder={PHONE_PLACEHOLDER}
            />
            <p className="mt-1 text-xs text-gray-500">
              {fieldErrors.phone ?? `Enter a valid phone number, e.g. ${PHONE_EXAMPLE_PRETTY}`}
            </p>
          </div>
          <div>
            <label htmlFor="accept-password" className="block text-xs font-medium text-gray-400 mb-1">
              Password
            </label>
            <input
              id="accept-password"
              type="password"
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              required
              disabled={submitting}
              minLength={8}
              className={inputClass}
              placeholder="At least 8 characters"
            />
            {fieldErrors.password && (
              <p className="mt-1 text-xs text-red-300">{fieldErrors.password}</p>
            )}
          </div>

          {submitError && (
            <div className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-100 text-sm">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {submitting ? 'Accepting…' : `Accept invite & continue`}
          </button>

          <p className="text-center text-xs text-gray-500 pt-2">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
